import SelectComponent from '@components/inputs/select/Select.component';
import { Colors } from '@constants/colors.constants';
import { Sizes } from '@constants/sizes.constants';
import { Close } from '@icons/index';
import { Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import {
  AuditInformation,
  AuditInformationCreatedFromStepEnum,
  AuditInformationStatusEnum,
  AuditInformationTypeEnum,
  CreateAuditInformation
} from '@store/audits/audits-information/audits-information.model';
import { PreAuditType } from '@store/audits/pre-audits/pre-audits.model';
import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import PreAuditContact from './components/PreAuditContact';
import PreAuditData from './components/PreAuditData';
import PreAuditNeeds from './components/PreAuditNeeds';
import PreAuditObservation from './components/PreAuditObservation';

interface LocationState {
  auditInformation?: AuditInformation;
}

const AddPreAuditDataDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { auditId } = useParams();
  const [auditIdParsed, setAuditIdParsed] = useState<number>();
  const [loading, setLoading] = useState<boolean>();
  const [auditInfo, setAuditInfo] = useState<CreateAuditInformation>({
    type: AuditInformationTypeEnum.CONTACT
  });
  const location: LocationState = useLocation().state as LocationState;
  const auditInformation = location?.auditInformation;

  const errorsContact = {
    role: getFieldError(auditInfo?.contact?.role, FieldValidationType.REQUIRED_STRING),
    phoneNumber: getFieldError(auditInfo?.contact?.phoneNumber, FieldValidationType.REQUIRED_PHONE_NUMBER_WITH_CODE),
  };

  const errorsNeeds = {
    description: getFieldError(auditInfo?.need?.description, FieldValidationType.REQUIRED_STRING_LONG),
  };

  const errorsData = {
    legend: getFieldError(auditInfo?.data?.legend, FieldValidationType.REQUIRED_STRING_LONG),
    description: getFieldError(auditInfo?.data?.description, FieldValidationType.REQUIRED_STRING_LONG),
  };

  const errorsObservation = {
    observation: getFieldError(auditInfo?.observation?.observation, FieldValidationType.REQUIRED_STRING_LONG),
  };

  const getError = (type: AuditInformationTypeEnum) => {
    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        return errorsContact;
      case AuditInformationTypeEnum.DATA:
        return errorsData;
      case AuditInformationTypeEnum.NEED:
        return errorsNeeds;
      case AuditInformationTypeEnum.OBSERVATION:
        return errorsObservation;
      default:
        return errorsContact;
    }

  };

  const updateAuditInformation = (field: keyof CreateAuditInformation, value: any) => {
    setAuditInfo((createAuditInformation) => {
      if (field === 'type') {
        // const { type, ...rest } = createAuditInformation;
        return {
          type: value
        };
      } else {
        return {
          ...createAuditInformation,
          [field]: {
            ...(createAuditInformation[field] || {}),
            ...value,
          },
        };
      }
    });
  };

  function loadAuditInformation(auditInformation: AuditInformation) {

    const { type, status, documents, description, lastName, firstName, role, email, phoneNumber, codePhone, data, legend } = auditInformation;

    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        updateAuditInformation('type', AuditInformationTypeEnum.CONTACT);
        updateAuditInformation('contact', { lastName, firstName, role, email, phoneNumber, codePhone, status });
        break;

      case AuditInformationTypeEnum.NEED:
        updateAuditInformation('type', AuditInformationTypeEnum.NEED);
        updateAuditInformation('need', { description });
        updateAuditInformation('need', { status: status });
        break;

      case AuditInformationTypeEnum.DATA:
        updateAuditInformation('type', AuditInformationTypeEnum.DATA);
        updateAuditInformation('data', { description });
        updateAuditInformation('data', { legend });
        updateAuditInformation('data', { unit: data });
        updateAuditInformation('data', { status: status });
        break;

      case AuditInformationTypeEnum.OBSERVATION:
        updateAuditInformation('type', AuditInformationTypeEnum.OBSERVATION);
        updateAuditInformation('observation', { observation: description, photos: documents, status });
        break;

      default:
        break;
    }
  }


  useEffect(() => {
    if (auditId) {
      setAuditIdParsed(parseInt(auditId!, 10));
    }

    if (auditInformation) {
      loadAuditInformation(auditInformation);
    }

  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [auditId, auditInformation]);



  const handleClose = () => navigate(-1);

  function handleCreate() {
    setLoading(true);
    const auditId = auditIdParsed;

    if (auditInfo.type === AuditInformationTypeEnum.CONTACT) {
      if (auditInfo.contact) {
        auditInfo.contact.auditId = auditId;
        auditInfo.contact.createdFrom = AuditInformationCreatedFromStepEnum.PREAUDIT;
      }

      const createOrUpdate = auditInformation
        ? auditsInformationService.updateAuditInformationContact(auditInfo.contact!, auditInformation.id)
        : auditsInformationService.createAuditInformationContact(auditInfo.contact!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(
              auditInformation
                ? t('audits.success.pre-audit.update-contact')
                : t('audits.success.pre-audit.create-contact'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else if (auditInfo.type === AuditInformationTypeEnum.NEED) {
      if (auditInfo.need) {
        auditInfo.need.auditId = auditId;
        auditInfo.need.createdFrom = AuditInformationCreatedFromStepEnum.PREAUDIT;

      }
      updateAuditInformation('need', { createdFrom: AuditInformationCreatedFromStepEnum.PREAUDIT });
      updateAuditInformation('need', { auditId });

      const createOrUpdate = auditInformation
        ? auditsInformationService.updateAuditInformationNeed(auditInfo.need!, auditInformation.id)
        : auditsInformationService.createAuditInformationNeed(auditInfo.need!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(auditInformation
              ? t('audits.success.pre-audit.update-need')
              : t('audits.success.pre-audit.create-need'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else if (auditInfo.type === AuditInformationTypeEnum.DATA) {
      if (auditInfo.data) {
        auditInfo.data.createdFrom = AuditInformationCreatedFromStepEnum.PREAUDIT;
        auditInfo.data.auditId = auditId;
      }

      updateAuditInformation('data', { auditId });
      updateAuditInformation('data', { createdFrom: AuditInformationCreatedFromStepEnum.PREAUDIT });

      const createOrUpdate = auditInformation
        ? auditsInformationService.updateAuditInformationData(auditInfo.data!, auditInformation.id)
        : auditsInformationService.createAuditInformationData(auditInfo.data!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(auditInformation
              ? t('audits.success.pre-audit.update-data')
              : t('audits.success.pre-audit.create-data'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else if (auditInfo.type === AuditInformationTypeEnum.OBSERVATION) {
      if (auditInfo.observation) {
        auditInfo.observation.createdFrom = AuditInformationCreatedFromStepEnum.PREAUDIT;
        auditInfo.observation.auditId = auditId;
      }
      console.log(auditInfo.observation);

      const createOrUpdate = auditInformation
        ? auditsInformationService.updateAuditInformationObservation(auditInfo.observation!, auditInformation.id)
        : auditsInformationService.createAuditInformationObservation(auditInfo.observation!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(auditInformation
              ? t('audits.success.pre-audit.update-observation')
              : t('audits.success.pre-audit.create-observation'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  }

  const getStringType = (type: AuditInformationTypeEnum) => {
    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        return 'contact';
      case AuditInformationTypeEnum.DATA:
        return 'data';
      case AuditInformationTypeEnum.NEED:
        return 'need';
      case AuditInformationTypeEnum.OBSERVATION:
        return 'observation';
      case AuditInformationTypeEnum.MAP:
        return 'map';
      default:
        return 'contact';
    }
  };

  const getStatusByType = (type: AuditInformationTypeEnum) => {
    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        return auditInfo?.contact?.status;
      case AuditInformationTypeEnum.DATA:
        return auditInfo?.data?.status;
      case AuditInformationTypeEnum.NEED:
        return auditInfo?.need?.status;
      case AuditInformationTypeEnum.OBSERVATION:
        return auditInfo?.observation?.status;
      case AuditInformationTypeEnum.MAP:
        return auditInfo?.map?.status;
      default:
        return auditInfo?.contact?.status;
    }
  };

  return (
    <>
      <Drawer anchor="right" open onClose={handleClose}>
        <Stack overflow="hidden" height="100%">
          <Stack direction="row" justifyContent="flex-end">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <Stack pr="10px" flexGrow={1} display="flex" flexDirection="column">
            <Stack direction="row" alignItems="center" mb="15px" spacing={1.5}>
              <Typography fontSize={24} fontWeight="700">
                {auditInformation ? t('audits.preAudit.updateData') : t('audits.preAudit.addData')}
              </Typography>
            </Stack>
            {auditInformation?.author && (
              <Typography fontSize={12} fontWeight="400" mb="3px" color={Colors.textWithOpacity}>
                {t('global.createdBy', { by: { firstName: auditInformation?.author?.firstName, lastName: auditInformation?.author?.lastName } })}
              </Typography>
            )}
            <Divider variant="fullWidth" orientation="horizontal" />
            <Stack py="24px" pr="10px" spacing={1}>
              <SelectComponent
                color="primary"
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('audits.preAudit.type')}
                placeholder={t('global.select')}
                handleChange={(value) => { updateAuditInformation('type', value); }}
                items={PreAuditType.selectItems()}
                value={auditInfo?.type ?? ''}
              />
              {auditInformation && (
                <SelectComponent
                  color="primary"
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('audits.preAudit.status')}
                  placeholder={t('global.select')}
                  handleChange={(value) => { updateAuditInformation(getStringType(auditInfo?.type ?? AuditInformationTypeEnum.CONTACT), { status: value }); }}
                  items={AuditInformationStatusEnum.selectItems()}
                  value={getStatusByType(auditInfo?.type) ?? null}
                />
              )}
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal" />
            {[
              { type: 'CONTACT', component: <PreAuditContact errors={errorsContact} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> },
              { type: 'NEED', component: <PreAuditNeeds errors={errorsNeeds} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> },
              { type: 'DATA', component: <PreAuditData errors={errorsData} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> },
              { type: 'OBSERVATION', component: <PreAuditObservation errors={errorsObservation} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> }
            ].map(item => auditInfo?.type === item.type && <div key={item.type}>{item.component}</div>)}

            <Stack flexGrow={1} />
            <Stack mt="3px" direction="row" spacing={2} justifyContent="center" mb={1}>
              <Button variant="contained" color="primary" onClick={handleCreate} disabled={loading || checkIfErrors(getError(auditInfo.type))}>
                {auditInformation ? 'Modifier' : t('global.add')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
      <Outlet />
    </>
  );
};

export default AddPreAuditDataDrawer;
